import {
  ClipboardListIcon,
  ClockIcon,
  MailIcon,
} from '@heroicons/react/outline';
import classNames from 'classnames';
import { formatScheduledMailAt, getTimeStamp } from '../lib/time';
import MailStepBadge from './MailStepBadge';

interface FollowUpSelectorProps {
  id: string;
  isIntake?: boolean;
  subject?: string;
  providerNote?: string;
  createdAt?: string;
  /** Calendar date (YYYY-MM-DD) when this is scheduled mail */
  scheduledDate?: string;
  /** Wall-clock send time (HH:mm or HH:mm:ss) when this is scheduled mail */
  scheduledTime?: string;
  /** 0-based step index when this is scheduled mail */
  stepIndex?: number;
  sequenceTitle?: string;
  unreadCommentCount?: number;
  isScheduled?: boolean;
  isSelected: boolean;
  onClick: () => void;
  className?: string;
}

const FollowUpSelector: React.FC<FollowUpSelectorProps> = ({
  isIntake = false,
  subject,
  providerNote,
  createdAt,
  scheduledDate,
  scheduledTime,
  stepIndex,
  sequenceTitle,
  unreadCommentCount = 0,
  isScheduled = false,
  isSelected,
  onClick,
  className,
}) => {
  const Icon = isScheduled
    ? ClockIcon
    : isIntake
    ? ClipboardListIcon
    : MailIcon;

  const title =
    subject ||
    providerNote ||
    (isScheduled
      ? 'Scheduled mail'
      : createdAt
      ? getTimeStamp(createdAt, false)
      : 'Mail');

  const metaLabel = isScheduled ? 'Scheduled:' : 'Sent:';
  const metaValue = isScheduled
    ? scheduledDate
      ? formatScheduledMailAt(scheduledDate, scheduledTime)
      : ''
    : createdAt
    ? getTimeStamp(createdAt, false)
    : '';

  const showSequenceLine =
    isScheduled && stepIndex != null && Boolean(sequenceTitle);

  return (
    <div
      className={classNames(
        'mb-1.5 flex w-full shrink-0 cursor-pointer flex-row items-start justify-between gap-x-2 rounded-md py-3 pl-3 pr-3 hover:bg-neutral-25',
        isSelected &&
          (isScheduled
            ? 'bg-blue-25 font-medium hover:bg-blue-25'
            : 'bg-secondary-20 font-medium hover:bg-secondary-25'),
        className,
      )}
      onClick={onClick}
    >
      <div className="flex min-w-0 flex-1 flex-row items-start gap-x-2.5">
        <Icon
          className={classNames('mt-0.5 h-4 w-4 shrink-0', {
            'text-blue-100': isScheduled || isIntake,
            'text-secondary-100': !isScheduled && !isIntake,
          })}
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="text-caption font-medium leading-tight text-neutral-150 line-clamp-2">
            {title}
          </div>
          {showSequenceLine && (
            <div className="pt-1 text-small-label leading-snug text-neutral-125 line-clamp-2">
              <MailStepBadge
                stepIndex={stepIndex!}
                size="small"
                className="text-small-label text-neutral-125"
              />{' '}
              from{' '}
              <span className="font-medium text-neutral-150">
                {sequenceTitle}
              </span>
            </div>
          )}
          {metaValue && (
            <div className="truncate whitespace-nowrap pt-1 text-small-label text-neutral-125">
              <span className="font-medium">{metaLabel}</span> {metaValue}
            </div>
          )}
        </div>
      </div>
      {!isScheduled && unreadCommentCount > 0 && (
        <div className="mt-1.5 flex h-2 w-2 shrink-0 flex-row items-center justify-center rounded-xl bg-secondary-100" />
      )}
    </div>
  );
};

export default FollowUpSelector;
